/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { BundleType } from '../../resources/nls/bundle';
import type { Gantt as GanttProps, GanttRow, GanttTask } from '../Gantt.types';
import { GanttRowLayoutObj, GanttTaskLayoutObj, getNormalizedStartEnd } from './dataLayoutUtils';

const ARIA_LABEL_STATE_DELIMITER = ', ';
const ARIA_LABEL_DESC_DELIMITER = '. ';

// TODO: Move to a shared location?
/**
 * Produces an aria label by combining a given aria label and states.
 * @param accessibleLabel The accessible label.
 * @param states The states.
 * @returns Final aria label.
 */
const generateAriaLabel = (accessibleLabel?: string, states?: string[]) => {
  const desc = [];
  const hasStates = states && states.length > 0;

  if (accessibleLabel) {
    desc.push(accessibleLabel);
  }
  if (hasStates) {
    desc.push(states.join(ARIA_LABEL_STATE_DELIMITER));
  }
  return desc.join(ARIA_LABEL_DESC_DELIMITER);
};

/**
 * Gets the default aria label for the task.
 * @param taskObj The task layout object.
 * @param rowData The rows data.
 * @param dateFormatter The date formatter.
 * @param translations The translations bundle.
 * @returns The default aria label.
 */
const getDefaultTaskAccessibleLabel = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>(
  taskObj: GanttTaskLayoutObj<K2>,
  rowData: GanttProps<K2, D1, D2>['rows'],
  // TODO: take valueFormats instead of dateFormatter when we support the API
  dateFormatter: (date: string) => string,
  translations: BundleType
) => {
  const row = rowData[taskObj.rowIndex];
  const task = row.tasks[taskObj.index];

  const { start, end } = getNormalizedStartEnd(task);
  const isMilestone = start === end;

  const desc = [];
  if (row.label) {
    const rowDesc = translations.dataVisualization_labelAndValue({
      LABEL: translations.gantt_labelRow(),
      VALUE: row.label
    });
    desc.push(rowDesc);
  }
  if (!isMilestone) {
    const startDesc = translations.dataVisualization_labelAndValue({
      LABEL: translations.gantt_labelStart(),
      VALUE: dateFormatter(start)
    });
    const endDesc = translations.dataVisualization_labelAndValue({
      LABEL: translations.gantt_labelEnd(),
      VALUE: dateFormatter(end)
    });
    desc.push(startDesc);
    desc.push(endDesc);
  } else {
    const dateDesc = translations.dataVisualization_labelAndValue({
      LABEL: translations.gantt_labelDate(),
      VALUE: dateFormatter(start)
    });
    desc.push(dateDesc);
  }
  if (task.label) {
    const labelDesc = translations.dataVisualization_labelAndValue({
      LABEL: translations.gantt_labelLabel(),
      VALUE: task.label
    });
    desc.push(labelDesc);
  }
  return desc.join('; ');
};

/**
 * Gets the aria label for the task.
 * @param taskObj The task layout object.
 * @param rowData The rows data.
 * @param dateFormatter The date formatter.
 * @param translations The translations bundle.
 * @returns The aria label for the task.
 */
const getTaskAriaLabel = <K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>(
  taskObj: GanttTaskLayoutObj<K2>,
  rowData: GanttProps<K2, D1, D2>['rows'],
  dateFormatter: (date: string) => string,
  translations: BundleType
) => {
  const states: string[] = [];
  if (taskObj.isSelectable || taskObj.isSelected) {
    states.push(
      taskObj.isSelected
        ? translations.dataVisualization_stateSelected()
        : translations.dataVisualization_stateUnselected()
    );
  }

  const data = rowData[taskObj.rowIndex].tasks[taskObj.index];
  const accessibleLabel =
    data.accessibleLabel !== undefined
      ? data.accessibleLabel
      : getDefaultTaskAccessibleLabel<K1, K2, D1, D2>(
          taskObj,
          rowData,
          dateFormatter,
          translations
        );

  return generateAriaLabel(accessibleLabel, states);
};

/**
 * Gets the aria label for the row label
 * @param rowObj The row layout object.
 * @param rowData The rows data.
 * @returns The aria label for the row label.
 */
const getRowLabelAriaLabel = <K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>(
  rowObj: GanttRowLayoutObj<K1, K2>,
  rowData: GanttProps<K2, D1, D2>['rows']
) => {
  // TODO: There will be states when we support tree data
  const states: string[] = [];
  const data = rowData[rowObj.index];
  const accessibleLabel = data.accessibleLabel || data.label;
  return generateAriaLabel(accessibleLabel, states);
};

export { getTaskAriaLabel, getRowLabelAriaLabel };
